import React, { useState } from 'react';
import { CalendarClock, Sparkles, Receipt, ChevronDown, ChevronUp, FlaskConical } from 'lucide-react';

const STATE_STYLES = {
  aprovado: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  pendente: 'bg-amber-50 text-amber-700 ring-amber-200',
  pausado: 'bg-slate-100 text-slate-600 ring-slate-200',
  expirado: 'bg-rose-50 text-rose-700 ring-rose-200',
  rejeitado: 'bg-rose-50 text-rose-700 ring-rose-200',
  alugado: 'bg-sky-50 text-sky-700 ring-sky-200',
  vendido: 'bg-sky-50 text-sky-700 ring-sky-200',
  arquivado: 'bg-slate-100 text-slate-500 ring-slate-200',
};

const ACTION_LABELS = {
  pausar: 'Pausar anúncio',
  reativar: 'Reativar anúncio',
  marcar_alugado: 'Marcar como alugado',
  marcar_vendido: 'Marcar como vendido',
  arquivar: 'Arquivar',
};

const Pill = ({ children, cls = 'bg-slate-100 text-slate-600 ring-slate-200' }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ring-1 ${cls}`}>{children}</span>
);

const Section = ({ title, icon: Icon, children, testId, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
        data-testid={`${testId}-toggle`}
      >
        <span className="inline-flex items-center gap-2 text-xs font-bold text-slate-700">
          <Icon className="h-3.5 w-3.5 text-emerald-600" /> {title}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && <div className="border-t border-slate-100 px-3 py-2" data-testid={testId}>{children}</div>}
    </div>
  );
};

export default function ListingCommerceBlock({ data, busy, onAction, onOrder }) {
  if (!data) return null;
  const estado = data.estado_editorial;
  const ofertas = data.ofertas || [];
  const acoesEstado = (data.acoes || []).filter((a) => ACTION_LABELS[a]);

  return (
    <div className="mt-3 space-y-2" data-testid={`commerce-block-${data.listing_id}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Pill cls={STATE_STYLES[estado] || STATE_STYLES.arquivado}>{data.estado_editorial_label}</Pill>
        <Pill cls={data.estado_pagamento === 'pago' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-slate-100 text-slate-600 ring-slate-200'}>
          {data.estado_pagamento_label}
        </Pill>
        {data.modo_teste && (
          <Pill cls="bg-violet-50 text-violet-700 ring-violet-200"><FlaskConical className="mr-1 h-3 w-3" /> Modo de teste</Pill>
        )}
      </div>

      <Section title="Validade e pagamento" icon={CalendarClock} testId={`commerce-validity-${data.listing_id}`}>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600">
          <dt>Criado</dt><dd className="text-right font-semibold text-slate-800">{(data.criado_em || '').slice(0, 10) || '—'}</dd>
          <dt>Publicado</dt><dd className="text-right font-semibold text-slate-800">{(data.publicado_em || '').slice(0, 10) || '—'}</dd>
          <dt>Expira</dt><dd className="text-right font-semibold text-slate-800">{(data.expira_em || '').slice(0, 10) || '—'}</dd>
          <dt>Dias restantes</dt><dd className="text-right font-semibold text-slate-800" data-testid={`commerce-days-${data.listing_id}`}>{data.dias_restantes ?? '—'}</dd>
          <dt>Produto</dt><dd className="text-right font-semibold text-slate-800">{data.produto?.name || (data.origem === 'publicacao_existente' ? 'Publicação existente' : '—')}</dd>
          <dt>Preço pago</dt><dd className="text-right font-semibold text-slate-800">{data.preco_pago ? `${data.preco_pago} ${data.moeda}` : 'Sem cobrança'}</dd>
          <dt>Contactos recebidos</dt><dd className="text-right font-semibold text-slate-800">{data.estatisticas?.contactos_recebidos ?? 0}</dd>
          {data.estatisticas?.rastreio_visualizacoes_ativo && (
            <>
              <dt>Visualizações</dt><dd className="text-right font-semibold text-slate-800">{data.estatisticas.visualizacoes}</dd>
            </>
          )}
        </dl>
        {data.campanha_gratuita && (
          <p className="mt-2 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800">Campanha gratuita aplicada.</p>
        )}
        {data.gratuidade && (
          <div className="mt-2 rounded-lg bg-emerald-50 px-2 py-1.5 text-[11px] text-emerald-800" data-testid={`commerce-free-${data.listing_id}`}>
            <p className="font-bold">{data.gratuidade.label}</p>
            {data.gratuidade.campanha && <p>Campanha: {data.gratuidade.campanha}</p>}
            <p>
              Sem qualquer cobrança neste anúncio
              {data.gratuidade.expira_em ? ` · válido até ${data.gratuidade.expira_em.slice(0, 10)}` : ''}
              {data.gratuidade.dias_restantes != null ? ` (${data.gratuidade.dias_restantes} dia(s))` : ''}.
            </p>
            <p className="mt-0.5 opacity-80">Quando terminar, pode renovar ou prolongar nas ações do anúncio.</p>
          </div>
        )}
        {data.pagamento_pendente && (
          <p className="mt-2 rounded-lg bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid={`commerce-pending-${data.listing_id}`}>
            Existe um pagamento pendente. Novas compras ficam bloqueadas até ser confirmado.
          </p>
        )}
      </Section>

      <Section title={`Extras (${(data.extras || []).filter((e) => e.ativo).length} ativos)`} icon={Sparkles} testId={`commerce-extras-${data.listing_id}`} defaultOpen={false}>
        {(data.extras || []).length === 0 ? (
          <p className="text-[11px] text-slate-500">Sem extras ativos.</p>
        ) : (
          <ul className="space-y-1 text-[11px] text-slate-600">
            {data.extras.map((e) => (
              <li key={e.tipo} className="flex items-center justify-between gap-2">
                <span>{e.label}</span>
                <span className="font-semibold text-slate-800">
                  {e.ativo ? `${e.dias_restantes} dia(s)` : 'terminado'}
                  {e.ends_at ? ` · até ${e.ends_at.slice(0, 10)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`commerce-actions-${data.listing_id}`}>
        <p className="mb-2 text-xs font-bold text-slate-700">Ações do anúncio</p>
        <div className="flex flex-wrap gap-1.5">
          {acoesEstado.map((a) => (
            <button
              key={a}
              type="button"
              disabled={busy}
              onClick={() => onAction(data.listing_id, a)}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              data-testid={`commerce-action-${a}-${data.listing_id}`}
            >
              {ACTION_LABELS[a]}
            </button>
          ))}
          {acoesEstado.length === 0 && <p className="text-[11px] text-slate-500">Sem ações disponíveis neste estado.</p>}
        </div>

        <p className="mb-2 mt-3 text-xs font-bold text-slate-700">Prolongar, renovar e destacar</p>
        {ofertas.length === 0 ? (
          <p className="text-[11px] text-slate-500" data-testid={`commerce-no-offers-${data.listing_id}`}>
            Ainda não existem produtos disponíveis para este anúncio.
          </p>
        ) : (
          <div className="space-y-1.5">
            {ofertas.map((o) => (
              <div key={o.product_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5">
                <div className="min-w-0">
                  <p className="truncate text-[12px] font-semibold text-slate-800">{o.name}</p>
                  <p className="text-[10px] text-slate-500">
                    {o.dias ? `${o.dias} dias · ` : ''}
                    {o.gratuito ? 'Gratuito' : `${o.preco_final} ${o.moeda}`}
                    {o.campanha ? ` · ${o.campanha.nome}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busy || data.pagamento_pendente}
                  onClick={() => onOrder(data.listing_id, o)}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white transition hover:bg-emerald-700 disabled:opacity-50"
                  data-testid={`commerce-buy-${o.code}-${data.listing_id}`}
                >
                  {o.texto_botao || (o.gratuito ? 'Ativar' : 'Comprar')}
                </button>
              </div>
            ))}
          </div>
        )}
        {data.modo_teste && (
          <p className="mt-2 rounded-lg bg-violet-50 px-2 py-1 text-[10px] text-violet-800">
            Comércio desativado: as compras ficam registadas como pendentes, sem cobrança real.
          </p>
        )}
      </div>

      <Section title="Histórico comercial" icon={Receipt} testId={`commerce-history-${data.listing_id}`} defaultOpen={false}>
        {(data.historico_comercial || []).length === 0 && (data.encomendas || []).length === 0 ? (
          <p className="text-[11px] text-slate-500">Sem movimentos comerciais.</p>
        ) : (
          <ul className="space-y-1 text-[11px] text-slate-600">
            {(data.historico_comercial || []).map((h, i) => (
              <li key={`h${i}`}>{(h.at || '').slice(0, 10)} · {h.descricao}</li>
            ))}
            {(data.encomendas || []).map((o) => (
              <li key={o.id}>{(o.created_at || '').slice(0, 10)} · {o.order_number} · {o.product_name} · {o.status}</li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
